/**
 * Web only (loaded by ProtectedWebShell): animates the workspace layout when
 * it changes. See `lib/web-motion.ts` for the model.
 *
 * The shell keeps rendering the layout it last SHOWED until the browser has
 * snapshotted it, then commits the new one inside the View Transition. That
 * lag is what lets a closing canvas panel slide out: `ArtifactPanelHost`
 * reads the shown panel from `ShownArtifactPanelContext`, so the old file is
 * still on screen for the "before" picture. Without View Transitions (or with
 * reduced motion) the new layout renders immediately, exactly as before.
 */
import { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';
import type { ArtifactPanelState } from '../artifacts/artifact-panel-store';
import {
  canAnimateWorkspace,
  classifyWorkspaceTransition,
  startWorkspaceTransition,
  type WorkspaceSnapshot,
} from '../../lib/web-motion';

export interface ShownWorkspace {
  className: string;
  panel: ArtifactPanelState;
}

/** The canvas panel the shell is showing (lags the store during a transition). */
export const ShownArtifactPanelContext = createContext<ArtifactPanelState | null>(null);

/** The shown panel inside the shell; the store's panel anywhere else. */
export function useShownArtifactPanel(fallback: ArtifactPanelState): ArtifactPanelState {
  return useContext(ShownArtifactPanelContext) ?? fallback;
}

function snapshot(workspace: ShownWorkspace): WorkspaceSnapshot {
  return { className: workspace.className, artifactId: workspace.panel.artifactId };
}

function sameSnapshot(a: WorkspaceSnapshot, b: WorkspaceSnapshot) {
  return a.className === b.className && a.artifactId === b.artifactId;
}

// The browser suppresses rendering until the update callback settles, so it
// must always settle: if the new layout somehow never commits, give up
// waiting after this long and let the transition finish.
const UPDATE_TIMEOUT_MS = 800;
// The browser always calls the update, even for a skipped transition. If it
// somehow has not within this long, show the new layout anyway.
const START_GUARD_MS = 1000;

interface RunningTransition {
  /** The layout this transition animates to. */
  to: WorkspaceSnapshot;
  /** Settles the update callback; set while the browser waits on it. */
  finishUpdate: (() => void) | null;
  /** The browser has called the update. */
  updated: boolean;
  skip: () => void;
}

export function useWorkspaceTransition(
  className: string,
  panel: ArtifactPanelState,
): ShownWorkspace {
  const target: ShownWorkspace = { className, panel };
  const [shown, setShown] = useState<ShownWorkspace>(target);
  const latest = useRef(target);
  latest.current = target;
  const running = useRef<RunningTransition | null>(null);

  const kind = classifyWorkspaceTransition(snapshot(shown), snapshot(target));
  const animate = kind !== null && canAnimateWorkspace();

  useLayoutEffect(() => {
    const run = running.current;
    if (run) {
      // The new layout is committed: the browser takes the "after" picture.
      if (!kind) {
        run.finishUpdate?.();
        return;
      }
      // Still waiting for the browser to call the update.
      if (sameSnapshot(run.to, snapshot(latest.current))) return;
      // The layout moved on mid-transition: drop the animation and show the
      // newest layout at once.
      run.skip();
      running.current = null;
      setShown(latest.current);
      return;
    }
    if (!kind) return;
    if (!animate) {
      setShown(latest.current);
      return;
    }
    const next: RunningTransition = {
      to: snapshot(latest.current),
      finishUpdate: null,
      updated: false,
      skip: () => {},
    };
    const transition = startWorkspaceTransition(
      kind,
      () =>
        new Promise<void>((resolve) => {
          const finish = () => {
            clearTimeout(timer);
            next.finishUpdate = null;
            resolve();
          };
          const timer = setTimeout(finish, UPDATE_TIMEOUT_MS);
          next.finishUpdate = finish;
          next.updated = true;
          setShown(latest.current);
        }),
    );
    // Could not start: the update already ran, unanimated.
    if (!transition) return;
    next.skip = () => transition.skipTransition();
    running.current = next;
    setTimeout(() => {
      if (running.current !== next || next.updated) return;
      running.current = null;
      next.skip();
      setShown(latest.current);
    }, START_GUARD_MS);
    void transition.finished
      .catch(() => {})
      .finally(() => {
        if (running.current === next) running.current = null;
      });
    // Runs whenever the shown or the wanted layout changes; `latest` carries
    // the full target (panel props included) without re-running on them.
  }, [kind, animate, shown, className, panel.artifactId]);

  // Between changes the shown panel follows the store, so a version or view
  // switch inside the same open file is never held back.
  return kind && animate ? shown : target;
}
